import capitalize from 'lodash/capitalize';
import kebabCase from 'lodash/kebabCase';
import words from 'lodash/words';
import uniqueId from 'lodash/uniqueId';
import sortBy from 'lodash/sortBy';

import * as dt from 'constants/esDataTypes';
import * as fc from 'utils/fieldConstants';
import { BaseOption } from 'ui/selects';
import { ESFieldTypeMap, ESTypeMap } from 'modules/workflows/types';

/**
 * Gets a unique id for an es datatype or field
 */
const getESTypeID = (name = ''): string => uniqueId(kebabCase(name));

const dataTypeDisplayNameMap = {
  [fc.BRAND_AFFINITY]: 'Brand Affinity Label',
  [dt.USER]: 'Contact Property',
  [dt.EMAIL_UNSUBSCRIBE]: 'Unsubscribe',
  [dt.EMAIL_SUBSCRIBE]: 'Subscribe',
  [dt.SMS_SEND]: 'SMS Send',
  [dt.SMS_BOUNCE]: 'SMS Bounce',
  [dt.SMS_RECEIVED]: 'SMS Reply',
};

const getDisplayName = (property = ''): string => words(property).map(capitalize).join(' ');

/**
 * Gets a readable name from an ES DataType
 * Taken from *segmentList.controller.js*, `$scope.esTypeToDisplayName`
 */
export const getESDataTypeDisplayName = (dataType = ''): string => {
  const displayName = dataTypeDisplayNameMap[dataType as keyof typeof dataTypeDisplayNameMap];

  if (displayName) return displayName;

  return displayName || getDisplayName(dataType);
};

const fieldDisplayNameMap = {
  [fc.BRAND_AFFINITY]: 'brandAffinityLabel',
  [fc.CAMPAIGN]: 'Campaign',
  [fc.CAMPAIGN_LABEL]: 'Campaign label',
  [fc.CONTENT_ID]: 'Content ID',
  [fc.EMAIL_DOMAIN]: 'Email domain',
  [fc.LIST_MEMBERSHIP]: 'List membership',
  [fc.USER_LIST_MEMBERSHIP]: 'List membership',
  [fc.LIST_MEMBERSHIP_SINGLE]: 'User list',
  [fc.MESSAGE_CHANNEL]: 'Message channel',
  [fc.MESSAGE_TYPE]: 'Message type',
  [fc.PHONE_COUNTRY]: 'Phone country',
  [fc.PHONE_TYPE]: 'Phone type',
  [fc.PHONE_NUMBER]: 'Phone number',
  [fc.PROFILE_UPDATED_DATE]: 'Profile updated date',
  [fc.RECURRING_CAMPAIGN]: 'Recurring campaign',
  [fc.SIGNUP_DATE]: 'Signup date',
  [fc.SIGNUP_SOURCE]: 'Signup source',
  [fc.SUBSCRIBED_MESSAGE_TYPES]: 'Is eligible to receive message types',
  [fc.TEMPLATE_ID]: 'Template ID',
  [fc.UNSUBSCRIBED_CHANNELS]: 'Unsubscribed channels',
  [fc.UNSUBSCRIBED_MESSAGE_TYPES]: 'Unsubscribed message types',
};

/**
 * Gets a readable name from an ES Field
 * Taken from *segmentList.controller.js*, `getFieldDisplayName`
 */
export const getESFieldDisplayName = (field = '', dataType = ''): string => {
  const displayName = fieldDisplayNameMap[field as keyof typeof fieldDisplayNameMap];

  if (displayName && typeof displayName === 'string') return displayName;

  if (field === fc.CREATION_DATE) {
    return `${dataType ? getESDataTypeDisplayName(dataType) : 'Creation'} Date`;
  }

  return field;
};

/**
 * Parses an ES DataType into the format that is required for ui/selects
 */
export const getESDataTypeSelectOption = (dataType: string): BaseOption => ({
  id: getESTypeID(dataType),
  label: getESDataTypeDisplayName(dataType),
  value: dataType,
});

/**
 * Parses an ES Field into the format that is required for ui/selects
 */
export const getESFieldSelectOption = (field: string, dataType: string): BaseOption => ({
  id: getESTypeID(field),
  label: getESFieldDisplayName(field, dataType),
  value: field,
});

const truthyFn = () => true;

interface ShowEsDataTypeFnArgs {
  /** dataType to filter by */
  dataType: string;
}

interface ShowEsFilterFieldFnArgs extends ShowEsDataTypeFnArgs {
  /** field to filter by */
  field: string;
  /** field type to filter by */
  fieldType: string | string[];
}

type ShowEsFilterFieldFn = (args: ShowEsFilterFieldFnArgs) => boolean;

interface GetESFieldSelectOptionsArgs {
  showField?: (args: ShowEsFilterFieldFnArgs) => boolean;
  sortCallback?: (arg: any[]) => any;
  isSorted?: boolean;
  sortByKey?: string;
}

/**
 * Returns an array of fields parsed for ui/selects options
 */
export const getESFieldSelectOptions = (
  fields: ESFieldTypeMap,
  dataType: string,
  {
    showField = truthyFn,
    isSorted = false,
    sortByKey = 'label',
    sortCallback,
  }: GetESFieldSelectOptionsArgs = {},
): BaseOption[] => {
  const options = Object.values(fields || {}).reduce(
    (acc, { fieldType, field }) =>
      showField({ field, fieldType, dataType })
        ? [...acc, getESFieldSelectOption(field, dataType)]
        : acc,
    [] as BaseOption[],
  );
  // if you don't want this to be sorted, return
  if (!isSorted) return options;

  // if they want to sort by something super nested, here is the override
  let cb = sortCallback;

  // otherwise use our default.
  if (!cb) {
    cb = (o) => {
      // `sortByKey` allows an easy way to implement sorting by another property
      const item = o[sortByKey as keyof typeof o];
      return typeof item === 'string' ? item.toLowerCase() : item;
    };
  }

  return sortBy(options, [cb]) as BaseOption[];
};

type ShowEsDataTypeFn = (args: ShowEsDataTypeFnArgs) => boolean;
interface GetESMappingsSelectOptionsArgs {
  /**
   * Should the field be shown?
   * Defaults to function returning `true`
   */
  showField?: ShowEsFilterFieldFn;
  /**
   * Should the dataType be shown?
   * Defaults to function returning `true`
   */
  showDataType?: ShowEsDataTypeFn;
}

interface FieldTypeOptionMap {
  [dataType: string]: BaseOption[];
}

interface GetESMappingsSelectOptionsResponse {
  /** Data Types converted into options for selects */
  dataTypes: BaseOption[];
  /** Field options for selects, keyed by data types */
  fieldMap: FieldTypeOptionMap;
}

/**
 * Returns the following:
 *
 * 1. An array of dataTypes parsed for ui/selects options
 * 2. An object containing fields parsed for ui/selects options, keyed by dataType
 *
 * @example
 * const mappings = {
 *  user: {campaignId: 'number', email: 'string', signupDate: 'date'},
 *  customEvent: {campaignId: 'number', email: 'string', signupDate: 'date'},
 *  purchase: {campaignId: 'number', email: 'string', createdAt: 'date'},
 * };
 *
 *
 * const {dataTypes, fieldMap} = getESMappingsSelectOptions(mappings, {
 *  showDataType: ({dataType}) => dataType !== 'user',
 *  showField: ({fieldType}) => fieldType === 'date',
 * });
 *
 * // The following will be returned
 * dataTypes: [{value: 'customEvent', id:..., label:...}, {value: 'purchase', id:..., label:...}]
 * fieldMap: {
 *  customEvent: [{value: 'signupDate', id:..., label:...}],
 *  purchase: [{value: 'createdAt', id:..., label:...}],
 * }
 */
export const getESMappingsSelectOptions = (
  mappings: ESTypeMap,
  opts: GetESMappingsSelectOptionsArgs = {},
): GetESMappingsSelectOptionsResponse => {
  const { showField = truthyFn, showDataType = truthyFn } = opts;

  return Object.entries(mappings).reduce(
    (acc, [dataType, fields]) =>
      showDataType({ dataType })
        ? {
            dataTypes: [...acc.dataTypes, getESDataTypeSelectOption(dataType)],
            fieldMap: {
              ...acc.fieldMap,
              [dataType]: getESFieldSelectOptions(fields, dataType, { showField, isSorted: true }),
            },
          }
        : acc,
    { dataTypes: [], fieldMap: {} } as GetESMappingsSelectOptionsResponse,
  );
};
